import { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  DocumentTextIcon,
  ArrowUpTrayIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ArrowsUpDownIcon,
  FunnelIcon,
  EyeIcon,
  TrashIcon,
  PlayIcon,
  PencilSquareIcon,
  FolderOpenIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  TagIcon,
  UserIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { handleError, handleSuccess } from '../../utils/errorHandler';
import useDocumentStore from '../../store/documentStore';
import useAuthStore from '../../store/authStore';
import { documentsAPI } from '../../services/api';
import { getDisplayTitle } from '../../utils/getDisplayTitle';

// Tier-1 verification badge — verdict → icon + colors + label
export function getVerificationBadge(verification, compact = true) {
  if (!verification?.verdict) return null;
  const flags = (verification.flags || []).map((f) => f.detail).join('\n');
  const map = {
    high_risk: {
      Icon: ShieldExclamationIcon, label: 'High risk',
      cls: 'bg-status-error/10 text-status-error border-status-error/30',
    },
    needs_review: {
      Icon: ShieldExclamationIcon, label: 'Review',
      cls: 'bg-status-warning/10 text-status-warning border-status-warning/30',
    },
    clear: {
      Icon: ShieldCheckIcon, label: 'Verified',
      cls: 'bg-status-success/10 text-status-success border-status-success/30',
    },
  };
  const cfg = map[verification.verdict];
  if (!cfg) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border ${cfg.cls}`}
      title={`Verification: ${cfg.label} (risk ${verification.risk_score}/100)\n${flags}`}
    >
      <cfg.Icon className="w-3 h-3" />
      {compact ? cfg.label : `${cfg.label} · ${verification.risk_score}/100`}
    </span>
  );
}

const filterOptions = [
  { value: 'all', label: 'All Documents' },
  { value: 'processed', label: 'Processed' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
];

const sortOptions = [
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'name', label: 'Name A-Z' },
  { value: 'name-desc', label: 'Name Z-A' },
];



export default function Documents() {
  const {
    documents,
    fetchDocuments,
    deleteDocument,
    isLoading,
    pagination,
    // Global processing state from store
    processingDocuments,
    startProcessingWithPolling
  } = useDocumentStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState('all'); // 'all' = search content, 'title' = names only
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('list');
  // Smart filters — detected document type + tagged customer (server-side)
  const [smartOptions, setSmartOptions] = useState({ types: [], customers: [] });
  const [typeFilter, setTypeFilter] = useState('');
  const [customerFilterId, setCustomerFilterId] = useState('');
  const [assignDoc, setAssignDoc] = useState(null); // doc being customer-tagged
  // Keep local state for batch processing and page info
  const [processingPageInfo, setProcessingPageInfo] = useState({}); // Store page counts
  const [showBatchProcessModal, setShowBatchProcessModal] = useState(false);
  const [batchEmailNotification, setBatchEmailNotification] = useState(false);
  const [pendingBatchDocs, setPendingBatchDocs] = useState([]);
  const [selectedDocs, setSelectedDocs] = useState([]);
  const { user } = useAuthStore();
  const isOwnDoc = (doc) => doc.user_id === user?.id;

  // Helper to check if document is processing (read directly from state for reactivity)
  const isProcessing = (docId) => !!processingDocuments[docId];
  // Helper to get processing progress (read directly from state for reactivity)
  const getProgress = (docId) => processingDocuments[docId]?.progress || 0;
  // Get list of all processing document IDs
  const processingIds = Object.keys(processingDocuments).map(id => parseInt(id, 10));

  // Elapsed time ticker for processing documents
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (processingIds.length === 0) return;
    const interval = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(interval);
  }, [processingIds.length]);

  const formatElapsed = useCallback((startedAt) => {
    if (!startedAt) return '';
    const seconds = Math.floor((Date.now() - startedAt) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }, [tick]); // tick dependency ensures re-render

  const pollingRef = useRef(null);
  const currentParamsRef = useRef({});
  const searchDebounceRef = useRef(null);

  // Cleanup search debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // NOTE: Polling is now handled exclusively in documentStore.js (startProcessingWithPolling)
  // The store's polling continues even when this component unmounts
  // Components should only READ processingDocuments state, not manage polling

  // Add navigation guard to warn when OCR is processing
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (processingIds.length > 0) {
        e.preventDefault();
        e.returnValue = 'OCR processing in progress. It will continue in the background.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [processingIds.length]);

  // Read search param from URL on initial load — use React Query for caching
  const urlSearch = searchParams.get('search');
  useEffect(() => {
    if (urlSearch) {
      setSearchQuery(urlSearch);
      setSearchParams({});
    }
  }, []);

  const initialParams = urlSearch ? { search: urlSearch } : {};
  useQuery({
    queryKey: ['documents-list', urlSearch || ''],
    queryFn: async () => {
      currentParamsRef.current = initialParams;
      const hasCached = documents.length > 0;
      await fetchDocuments(initialParams, { silent: hasCached });
      return true; // data lives in Zustand store
    },
    staleTime: 0, // always re-fetch on mount so restored docs appear immediately
  });

  // NOTE: Processing completion detection and toasts are handled in documentStore.js
  // The store's startProcessingWithPolling() shows toasts when processing completes

  // UNIFIED POLLING EFFECT - handles all polling needs in one place
  // This prevents race conditions from multiple effects calling fetchDocuments
  useEffect(() => {
    // Clear any existing polling
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }

    const hasProcessing = processingIds.length > 0;
    const hasPending = documents.some(d => !d.is_processed && !d.has_error);
    const hasExtracting = documents.some(d => d.pages_ready === false);

    // Don't poll if nothing needs updating
    if (!hasProcessing && !hasPending && !hasExtracting) return;

    // Use faster polling (2s) when extracting pages, normal (3s) otherwise
    const pollInterval = hasExtracting ? 2000 : 3000;

    pollingRef.current = setInterval(() => {
      // The store's isFetching lock prevents concurrent fetches
      fetchDocuments(currentParamsRef.current, { silent: true });
    }, pollInterval);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [processingIds.length, documents, fetchDocuments]);

  // Simple processing state management - no automatic stuck detection
  // Processing states are only managed by explicit user actions

  // Don't automatically restore processing states on page load
  // Only add to processing when user explicitly starts processing
  useEffect(() => {
    // Clear any phantom processing states on initial load
    if (documents.length > 0 && processingIds.length === 0) {
      // This is the initial load, don't restore any processing states
      return;
    }
  }, [documents.length]);

  // Helper function to warn when navigating during processing or block non-own docs
  const handleNavigationClick = (e, doc) => {
    if (!isOwnDoc(doc)) {
      e.preventDefault();
      return;
    }
    if (processingIds.includes(doc.id)) {
      const confirmed = window.confirm(
        'This document is currently being processed. Navigating away may interrupt the OCR process. Do you want to continue?'
      );
      if (!confirmed) {
        e.preventDefault();
      }
    }
  };

  // Load smart-filter options (detected types + customers with counts)
  const loadSmartOptions = useCallback(async () => {
    try {
      const res = await documentsAPI.filterOptions();
      setSmartOptions(res.data || { types: [], customers: [] });
    } catch {
      /* non-fatal — filters just stay empty */
    }
  }, []);
  useEffect(() => { loadSmartOptions(); }, [loadSmartOptions]);

  // Build fetch params combining search + smart filters
  const buildParams = useCallback((query = searchQuery, mode = searchMode,
                                   type = typeFilter, cust = customerFilterId) => {
    const params = {};
    if (query && query.trim()) { params.search = query; params.search_mode = mode; }
    if (type) params.doc_type = type;
    if (cust) params.customer_id = cust;
    return params;
  }, [searchQuery, searchMode, typeFilter, customerFilterId]);

  // Refetch when smart filters change (skip the initial mount — useQuery already fetched)
  const smartMountRef = useRef(false);
  useEffect(() => {
    if (!smartMountRef.current) { smartMountRef.current = true; return; }
    const params = buildParams();
    currentParamsRef.current = params;
    fetchDocuments(params);
  }, [typeFilter, customerFilterId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search function for real-time filtering
  const debouncedSearch = useCallback((query, mode = searchMode) => {
    // Clear existing timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    // Set new timeout for search
    searchDebounceRef.current = setTimeout(() => {
      const params = buildParams(query, mode);
      currentParamsRef.current = params;
      fetchDocuments(params);
    }, 300); // 300ms debounce delay
  }, [fetchDocuments, searchMode, buildParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Clear debounce timeout and search immediately on form submit
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    const params = buildParams();
    currentParamsRef.current = params;
    fetchDocuments(params);
  };

  const handleClearSearch = () => {
    // Clear debounce timeout
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    setSearchQuery('');
    const params = buildParams('');
    currentParamsRef.current = params;
    fetchDocuments(params);
  };

  // Tag / remap / untag a document's customer (from the assign modal)
  const handleAssignCustomer = async (doc, payload) => {
    try {
      const res = await documentsAPI.assignCustomer(doc.id, payload);
      const cust = res.data?.customer;
      toast.success(cust ? `Tagged to ${cust.name}` : 'Customer tag removed');
      setAssignDoc(null);
      fetchDocuments(currentParamsRef.current, { silent: true });
      loadSmartOptions();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update customer');
    }
  };

  // Handle search mode change
  const handleSearchModeChange = (mode) => {
    setSearchMode(mode);
    // Re-search with new mode if there's a query
    if (searchQuery.trim()) {
      debouncedSearch(searchQuery, mode);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await deleteDocument(id);
      setSelectedDocs(prev => prev.filter(docId => docId !== id));
      handleSuccess('Document moved to recycle bin');
    } catch (error) {
      handleError(error, 'Failed to delete document');
    }
  };

  // Multi-select handlers
  const handleSelectDoc = (id) => {
    setSelectedDocs((prev) =>
      prev.includes(id) ? prev.filter((docId) => docId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocs.length === sortedDocuments.length) {
      setSelectedDocs([]);
    } else {
      setSelectedDocs(sortedDocuments.map((doc) => doc.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocs.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedDocs.length} document(s)?`)) {
      return;
    }

    const toastId = toast.loading(`Deleting ${selectedDocs.length} documents...`);
    let successCount = 0;
    let failCount = 0;

    for (const id of selectedDocs) {
      try {
        await deleteDocument(id);
        successCount++;
      } catch (error) {
        failCount++;
        console.error(`Failed to delete document ${id}:`, error);
      }
    }

    setSelectedDocs([]);

    if (failCount === 0) {
      toast.success(`Successfully deleted ${successCount} document(s)`, { id: toastId });
    } else {
      toast.error(`Deleted ${successCount}, failed ${failCount}`, { id: toastId });
    }
  };

  const handleProcessAll = () => {
    // Get all unprocessed documents that have pages ready
    const unprocessedDocs = documents.filter(
      doc => !doc.is_processed && !processingIds.includes(doc.id) && doc.pages_ready && doc.pages_count > 0
    );

    if (unprocessedDocs.length === 0) {
      toast('No documents available to process', { icon: 'ℹ️' });
      return;
    }

    // Show modal with email notification option
    setPendingBatchDocs(unprocessedDocs);
    setShowBatchProcessModal(true);
  };

  const executeBatchProcessing = async () => {
    setShowBatchProcessModal(false);

    const docIds = pendingBatchDocs.map(d => d.id);

    toast.loading(`Starting batch processing of ${docIds.length} documents...`, {
      id: 'batch-process',
      duration: 3000
    });

    try {
      console.log('Starting batch processing for documents:', docIds);
      const response = await documentsAPI.processMultiple(docIds, 'openai', batchEmailNotification);
      console.log('Batch processing response:', response);

      if (!response.data || !response.data.success) {
        throw new Error(response.data?.error || 'Batch processing failed');
      }

      const successMessage = batchEmailNotification
        ? `Processing started for ${response.data.document_count} documents! You'll receive an email when complete.`
        : `Processing started for ${response.data.document_count} documents!`;

      toast.success(successMessage, {
        id: 'batch-process',
        duration: 5000
      });

      // Reset email notification checkbox
      setBatchEmailNotification(false);

      // Use the store's polling for each document (continues even when navigating away)
      docIds.forEach(id => {
        const doc = pendingBatchDocs.find(d => d.id === id);
        startProcessingWithPolling(id, doc?.title || `Document ${id}`);
      });

    } catch (error) {
      console.error('Batch processing error:', error);
      console.error('Error response:', error.response);

      let errorMsg = 'Failed to start batch processing';

      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
      } else if (error.message) {
        errorMsg = error.message;
      }

      toast.error(errorMsg, { id: 'batch-process' });
    }
  };

  const handleProcess = async (e, id) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    toast.loading('Starting OCR processing...', {
      id: `process-${id}`,
      duration: 2000
    });

    try {
      const response = await documentsAPI.process(id);
      console.log('Process API response:', response.data);

      toast.success('Processing started! Tracking real progress...', {
        id: `process-${id}`,
        duration: 5000
      });

      // Use the global store's polling (continues even when navigating away)
      const doc = documents.find(d => d.id === id);
      startProcessingWithPolling(id, doc?.title || `Document ${id}`);

    } catch (error) {
      console.error('Failed to process:', error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || 'Failed to start processing. Please try again.';
      toast.error(errorMsg, { id: `process-${id}` });
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    if (filter === 'processed') return doc.is_processed;
    if (filter === 'pending') return !doc.is_processed && !doc.has_error;
    if (filter === 'failed') return doc.has_error;
    return true;
  });

  const sortedDocuments = [...filteredDocuments].sort((a, b) => {
    switch (sortBy) {
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'name':
        return (a.title || '').localeCompare(b.title || '');
      case 'name-desc':
        return (b.title || '').localeCompare(a.title || '');
      case 'newest':
      default:
        return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  const getStatusBadge = (doc) => {
    if (doc.is_processed) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-status-success/15 text-status-success text-xs font-medium">
          <CheckCircleIcon className="w-3.5 h-3.5" />
          Processed
        </span>
      );
    }
    if (doc.has_error) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-status-error/15 text-status-error text-xs font-medium">
          <ExclamationCircleIcon className="w-3.5 h-3.5" />
          Failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-theme-muted/15 text-theme-secondary text-xs font-medium">
        <ClockIcon className="w-3.5 h-3.5" />
        Pending
      </span>
    );
  };

  const formatIdentity = (identity) => {
    if (!identity) return '';
    return identity.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getIdentityBadge = (identity) => {
    if (!identity) return null;
    if (identity.toLowerCase() === 'general_text') {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-theme-elevated text-theme-secondary border border-theme-primary">
          General Text
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-brand-orange/10 text-brand-orange border border-brand-orange/20">
        {formatIdentity(identity)}
      </span>
    );
  };

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {/* Page Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-brand-orange to-brand-amber flex items-center justify-center shadow-md shadow-brand-orange/25 flex-shrink-0">
            <DocumentTextIcon className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-base font-bold text-theme-primary leading-tight">Documents</h1>
            <div className="flex items-center flex-wrap gap-1 text-xs text-theme-secondary">
              <span>{pagination.total || documents.length} documents</span>
              {(() => {
                const typeCounts = {};
                sortedDocuments.forEach(doc => {
                  if (doc.document_identity) {
                    typeCounts[doc.document_identity] = (typeCounts[doc.document_identity] || 0) + 1;
                  }
                });
                const uniqueTypes = Object.keys(typeCounts);
                if (uniqueTypes.length === 0) return null;
                return (
                  <div className="flex items-center gap-1">
                    <span className="text-theme-muted">&bull;</span>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-theme-elevated text-theme-secondary border border-theme-primary">
                      {uniqueTypes.length === 1 ? uniqueTypes[0] : `${uniqueTypes.length} types`}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
        <Link
          to="/upload"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-brand-orange to-brand-amber text-white font-medium rounded-lg shadow-md shadow-brand-orange/25 hover:shadow-brand-orange/40 transition-all text-xs whitespace-nowrap flex-shrink-0"
        >
          <ArrowUpTrayIcon className="w-3.5 h-3.5" />
          Upload New
        </Link>
      </div>

      {/* Toolbar */}
      <div className="bg-theme-tertiary rounded-lg p-2 border border-theme-primary transition-colors duration-300">
        {/* Row 1: Search + mode toggle */}
        <div className="flex items-center gap-2 mb-2">
          <form onSubmit={handleSearch} className="flex-1">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-theme-muted" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  debouncedSearch(value);
                }}
                placeholder={searchMode === 'all' ? 'Search...' : 'Search names...'}
                className="w-full pl-8 pr-8 py-1.5 bg-theme-elevated border border-theme-primary rounded-lg text-xs text-theme-primary placeholder:text-theme-muted focus:outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 transition-all"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 rounded text-theme-muted hover:text-theme-primary transition-colors"
                >
                  <XMarkIcon className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </form>
          {/* Search Mode Toggle */}
          <div className="flex items-center bg-theme-elevated rounded-md p-0.5 border border-theme-primary flex-shrink-0">
            <button
              type="button"
              onClick={() => handleSearchModeChange('all')}
              className={`px-2 py-1 rounded text-[10px] font-medium transition-colors whitespace-nowrap ${searchMode === 'all' ? 'bg-brand-orange text-white' : 'text-theme-muted hover:text-theme-primary'}`}
              title="All content"
            >
              All
            </button>
            <button
              type="button"
              onClick={() => handleSearchModeChange('title')}
              className={`px-2 py-1 rounded text-[10px] font-medium transition-colors whitespace-nowrap ${searchMode === 'title' ? 'bg-brand-orange text-white' : 'text-theme-muted hover:text-theme-primary'}`}
              title="Names only"
            >
              Name
            </button>
          </div>
        </div>
        {/* Row 2: Filter + Sort + View toggle */}
        <div className="flex items-center gap-1.5">
          <FunnelIcon className="w-3 h-3 text-theme-muted flex-shrink-0" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 min-w-0 px-2 py-1 bg-theme-elevated border border-theme-primary text-theme-primary text-xs rounded-md focus:outline-none focus:border-brand-orange transition-all"
          >
            {filterOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <ArrowsUpDownIcon className="w-3 h-3 text-theme-muted flex-shrink-0" />
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="flex-1 min-w-0 px-2 py-1 bg-theme-elevated border border-theme-primary text-theme-primary text-xs rounded-md focus:outline-none focus:border-brand-orange transition-all"
          >
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          {/* Smart filter: detected document type */}
          <TagIcon className="w-3 h-3 text-theme-muted flex-shrink-0" />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className={`flex-1 min-w-0 px-2 py-1 bg-theme-elevated border text-xs rounded-md focus:outline-none focus:border-brand-orange transition-all ${typeFilter ? 'border-brand-orange text-brand-orange' : 'border-theme-primary text-theme-primary'}`}
            title="Filter by detected document type"
          >
            <option value="">All Types</option>
            {smartOptions.types.map((t) => (
              <option key={t.value} value={t.value}>
                {formatIdentity(t.value)} ({t.count})
              </option>
            ))}
          </select>
          {/* Smart filter: customer */}
          <UserIcon className="w-3 h-3 text-theme-muted flex-shrink-0" />
          <select
            value={customerFilterId}
            onChange={(e) => setCustomerFilterId(e.target.value)}
            className={`flex-1 min-w-0 px-2 py-1 bg-theme-elevated border text-xs rounded-md focus:outline-none focus:border-brand-orange transition-all ${customerFilterId ? 'border-brand-orange text-brand-orange' : 'border-theme-primary text-theme-primary'}`}
            title="Filter by customer"
          >
            <option value="">All Customers</option>
            {smartOptions.customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name} ({c.count})
              </option>
            ))}
          </select>
          <div className="flex items-center bg-theme-elevated rounded-md p-0.5 border border-theme-primary flex-shrink-0">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-1 rounded transition-colors ${viewMode === 'grid' ? 'bg-brand-orange text-white' : 'text-theme-muted hover:text-theme-primary'}`}
              title="Grid view"
            >
              <Squares2X2Icon className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-1 rounded transition-colors ${viewMode === 'list' ? 'bg-brand-orange text-white' : 'text-theme-muted hover:text-theme-primary'}`}
              title="List view"
            >
              <ListBulletIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Batch Processing Banner */}
      {!isLoading && sortedDocuments.filter(doc => !doc.is_processed && doc.pages_ready && doc.pages_count > 0 && !processingIds.includes(doc.id)).length > 0 && (
        <div className="bg-theme-elevated rounded-lg border border-theme-primary p-4 transition-colors duration-300">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <div className="p-2 bg-brand-orange/10 rounded-lg">
                <svg className="w-5 h-5 text-brand-orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-semibold text-theme-primary">
                  {sortedDocuments.filter(doc => !doc.is_processed && doc.pages_ready && doc.pages_count > 0 && !processingIds.includes(doc.id)).length} document(s) ready to process
                </p>
                <p className="text-xs text-theme-muted">
                  Save time by processing all documents at once
                </p>
              </div>
            </div>
            <button
              onClick={handleProcessAll}
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-orange text-white rounded-lg font-medium hover:bg-brand-amber hover:shadow-lg transition-all text-sm whitespace-nowrap"
            >
              <PlayIcon className="w-4 h-4" />
              Process All
            </button>
          </div>
        </div>
      )}

      {/* Selection Toolbar */}
      {!isLoading && sortedDocuments.length > 0 && (
        <div className="bg-theme-tertiary rounded-lg border border-theme-primary p-3 transition-colors duration-300">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedDocs.length === sortedDocuments.length && sortedDocuments.length > 0}
                  onChange={handleSelectAll}
                  className="w-4 h-4 rounded border-theme-primary text-brand-orange focus:ring-brand-orange/50 bg-theme-elevated"
                />
                <span className="text-sm text-theme-secondary">
                  {selectedDocs.length === sortedDocuments.length && sortedDocuments.length > 0
                    ? 'Deselect All'
                    : 'Select All'}
                </span>
              </label>
              {selectedDocs.length > 0 && (
                <span className="text-sm text-theme-muted">
                  ({selectedDocs.length} selected)
                </span>
              )}
            </div>
            {selectedDocs.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="inline-flex items-center gap-2 px-4 py-2 bg-status-error/10 text-status-error rounded-lg font-medium hover:bg-status-error/20 transition-all text-sm"
              >
                <TrashIcon className="w-4 h-4" />
                Delete Selected ({selectedDocs.length})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Documents */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner-theme w-10 h-10" />
        </div>
      ) : sortedDocuments.length === 0 ? (
        <div className="bg-theme-tertiary rounded-lg border border-theme-primary p-8 transition-colors duration-300">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-14 h-14 rounded-lg bg-theme-elevated flex items-center justify-center mb-3">
              <FolderOpenIcon className="w-7 h-7 text-theme-muted" />
            </div>
            <h3 className="text-lg font-semibold text-theme-primary mb-1">No documents found</h3>
            <p className="text-sm text-theme-muted mb-4 max-w-md">
              {searchQuery || filter !== 'all'
                ? 'Try adjusting your search or filter criteria.'
                : 'Upload your first document to get started with OCR processing.'}
            </p>
            {!searchQuery && filter === 'all' && (
              <Link
                to="/upload"
                className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand-orange to-brand-amber text-white font-medium rounded-lg shadow-md shadow-brand-orange/25 hover:shadow-brand-orange/40 transition-all text-sm"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                Upload Document
              </Link>
            )}
          </div>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3">
          {sortedDocuments.map((doc) => (
            <div
              key={doc.id}
              className={`bg-theme-tertiary rounded-xl border overflow-hidden hover:border-theme-accent transition-all group ${selectedDocs.includes(doc.id) ? 'border-brand-orange ring-2 ring-brand-orange/30' : 'border-theme-primary'
                }`}
            >
              {/* Thumbnail */}
              <div className="relative">
                {/* Selection Checkbox */}
                <div className="absolute top-2 left-2 z-30">
                  <label className="block p-3 -m-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedDocs.includes(doc.id)}
                      onChange={() => handleSelectDoc(doc.id)}
                      className="w-5 h-5 rounded border-white/50 text-brand-orange focus:ring-brand-orange/50 bg-black/30 cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </label>
                </div>
                <Link
                  to={`/documents/${doc.slug || doc.id}`}
                  onClick={(e) => handleNavigationClick(e, doc)}
                  className="block aspect-[3/2] bg-theme-elevated relative overflow-hidden"
                >
                  {doc.pages?.[0]?.image || doc.thumbnail ? (
                    <img
                      src={doc.pages?.[0]?.image || doc.thumbnail}
                      alt=""
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <DocumentTextIcon className="w-10 h-10 text-theme-muted" />
                    </div>
                  )}

                  {/* Processing Overlay - only show if actually processing AND not yet marked as processed */}
                  {isProcessing(doc.id) && !doc.is_processed && (
                    <div className="absolute inset-0 bg-gradient-to-br from-brand-orange/90 to-brand-amber/90 backdrop-blur-sm flex flex-col items-center justify-center text-white z-10">
                      {/* Circular Progress */}
                      <div className="relative w-16 h-16 mb-2">
                        <svg className="w-16 h-16 transform -rotate-90">
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="rgba(255,255,255,0.3)"
                            strokeWidth="4"
                            fill="none"
                          />
                          <circle
                            cx="32"
                            cy="32"
                            r="28"
                            stroke="white"
                            strokeWidth="4"
                            fill="none"
                            strokeDasharray={`${2 * Math.PI * 28}`}
                            strokeDashoffset={`${2 * Math.PI * 28 * (1 - (getProgress(doc.id) || 0) / 100)}`}
                            className="transition-all duration-500"
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-lg font-bold">{Math.round(getProgress(doc.id) || 0)}%</span>
                        </div>
                      </div>
                      <p className="text-sm font-semibold">Processing...</p>
                      {processingDocuments[doc.id]?.startedAt && (
                        <p className="text-xs opacity-90 mt-0.5">{formatElapsed(processingDocuments[doc.id].startedAt)}</p>
                      )}
                      {processingPageInfo[doc.id] && (
                        <p className="text-xs opacity-90 mt-0.5">
                          {processingPageInfo[doc.id].processed} / {processingPageInfo[doc.id].total} pages
                        </p>
                      )}
                    </div>
                  )}

                  <div className="absolute top-2 right-2 z-20">
                    {getStatusBadge(doc)}
                  </div>
                </Link>
              </div>

              {/* Info */}
              <div className="p-2.5">
                <Link
                  to={`/documents/${doc.slug || doc.id}`}
                  onClick={(e) => handleNavigationClick(e, doc)}
                >
                  <h3 className="font-medium text-sm text-theme-primary group-hover:text-brand-orange transition-colors truncate">
                    {getDisplayTitle(doc)}
                  </h3>
                </Link>
                <div className="flex items-center gap-1.5 mt-0.5 text-xs text-theme-muted">
                  <span>{doc.file_type?.toUpperCase() || 'PDF'}</span>
                  <span>&bull;</span>
                  <span>{doc.pages_count ?? doc.pages?.length ?? 0} pg</span>
                </div>

                {/* Search Match Snippet */}
                {searchQuery && doc.match_source && doc.match_source !== 'title' && (
                  <div className="mt-1.5 p-1.5 bg-brand-amber/10 rounded border border-brand-amber/20">
                    <div className="flex items-center gap-1 text-[10px] text-brand-amber font-medium mb-0.5">
                      <MagnifyingGlassIcon className="w-3 h-3" />
                      <span>
                        {doc.match_source === 'content' ? 'Found in content' :
                          doc.match_source === 'content_en' ? 'Found in translation' :
                            doc.match_source === 'type' ? 'Matched type' : 'Match'}
                      </span>
                    </div>
                    {doc.match_snippet && (
                      <p
                        className="text-[10px] text-theme-secondary line-clamp-2 leading-relaxed"
                        dir={doc.match_source === 'content' ? 'rtl' : 'ltr'}
                      >
                        {doc.match_snippet}
                      </p>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center gap-1 mt-2 pt-2 border-t border-theme-primary">
                  {/* Extracting pages state */}
                  {!doc.pages_ready && doc.pages_count === 0 && (
                    <div className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-blue-500/10 text-blue-400 rounded-lg text-xs font-medium cursor-not-allowed">
                      <div className="w-3 h-3 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
                      Extracting...
                    </div>
                  )}
                  {/* Process button - only for own docs */}
                  {isOwnDoc(doc) && !doc.is_processed && !isProcessing(doc.id) && doc.pages_ready && doc.pages_count > 0 && (
                    <button
                      type="button"
                      onClick={(e) => handleProcess(e, doc.id)}
                      className="flex-1 inline-flex items-center justify-center gap-1 px-2 py-1.5 bg-brand-orange/10 text-brand-orange rounded-lg hover:bg-brand-orange/20 transition-colors text-xs font-medium"
                      title="Process document with OCR"
                    >
                      <PlayIcon className="w-3.5 h-3.5" />
                      Process
                    </button>
                  )}
                  {/* Processing state - only show if actually processing AND not yet marked as processed */}
                  {isOwnDoc(doc) && isProcessing(doc.id) && !doc.is_processed && (
                    <div className="flex-1 inline-flex flex-col items-center justify-center gap-0.5 px-2 py-1.5 bg-gradient-to-r from-brand-orange/20 to-brand-amber/20 text-brand-orange border border-brand-orange/30 rounded-lg text-xs font-semibold">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-3 border-2 border-brand-orange/30 border-t-brand-orange rounded-full animate-spin" />
                        <span>{Math.round(getProgress(doc.id) || 0)}%</span>
                      </div>
                      <span className="text-[10px] opacity-80">
                        {processingDocuments[doc.id]?.startedAt
                          ? formatElapsed(processingDocuments[doc.id].startedAt)
                          : processingPageInfo[doc.id]
                            ? `${processingPageInfo[doc.id].processed}/${processingPageInfo[doc.id].total} pages`
                            : 'Processing'}
                      </span>
                    </div>
                  )}
                  {isOwnDoc(doc) && (
                    <Link
                      to={`/documents/${doc.slug || doc.id}/crop`}
                      className="p-1.5 rounded-lg text-theme-muted hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                      title="Edit pages"
                    >
                      <PencilSquareIcon className="w-4 h-4" />
                    </Link>
                  )}
                  {isOwnDoc(doc) && (
                    <Link
                      to={`/documents/${doc.slug || doc.id}`}
                      className="p-1.5 rounded-lg text-theme-muted hover:text-brand-orange hover:bg-brand-orange/10 transition-colors"
                      title="View"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </Link>
                  )}
                  {isOwnDoc(doc) && (
                    <button
                      onClick={() => setAssignDoc(doc)}
                      className={`p-1.5 rounded-lg transition-colors ${doc.customer ? 'text-blue-400 hover:bg-blue-500/10' : 'text-theme-muted hover:text-blue-400 hover:bg-blue-500/10'}`}
                      title={doc.customer ? `Customer: ${doc.customer.name} — click to change` : 'Tag to a customer'}
                    >
                      <UserIcon className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(doc.id)}
                    className="p-1.5 rounded-lg text-theme-muted hover:text-status-error hover:bg-status-error/10 transition-colors"
                    title="Delete"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* List View */
        <div className="bg-theme-tertiary rounded-lg border border-theme-primary overflow-hidden transition-colors duration-300">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-theme-elevated">
                <th className="px-4 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                  Document
                </th>
                <th className="hidden sm:table-cell px-4 py-3 text-center text-xs font-semibold text-theme-muted uppercase tracking-wider w-20">
                  Pages
                </th>
                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                  Uploaded By
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-semibold text-theme-muted uppercase tracking-wider">
                  Created
                </th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-theme-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme-primary">
              {sortedDocuments.map((doc) => (
                <tr
                  key={doc.id}
                  className={`hover:bg-theme-surface transition-colors group ${selectedDocs.includes(doc.id) ? 'bg-brand-orange/5' : ''
                    }`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={selectedDocs.includes(doc.id)}
                        onChange={() => handleSelectDoc(doc.id)}
                        className="w-4 h-4 rounded border-theme-primary text-brand-orange focus:ring-brand-orange/50 bg-theme-elevated cursor-pointer flex-shrink-0"
                      />
                    <Link
                      to={`/documents/${doc.slug || doc.id}`}
                      onClick={(e) => handleNavigationClick(e, doc)}
                      className="flex items-center gap-3 flex-1 min-w-0"
                    >
                      <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-theme-elevated border border-theme-primary overflow-hidden group-hover:border-brand-orange/50 transition-colors">
                        {doc.pages?.[0]?.image || doc.thumbnail ? (
                          <img
                            src={doc.pages?.[0]?.image || doc.thumbnail}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center"><svg class="w-5 h-5 text-theme-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg></div>';
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <DocumentTextIcon className="w-5 h-5 text-theme-muted" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-theme-primary group-hover:text-brand-orange truncate transition-colors">
                          {getDisplayTitle(doc)}
                        </p>
                        {(doc.segments_count > 1 || doc.document_identity || doc.customer || doc.verification) && (
                          <div className="mt-1 flex items-center gap-1 flex-wrap">
                            {doc.segments_count > 1 ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-brand-orange/10 text-brand-orange border border-brand-orange/20">
                                <Squares2X2Icon className="w-3 h-3" />
                                {doc.segments_count} documents
                              </span>
                            ) : (
                              getIdentityBadge(doc.document_identity)
                            )}
                            {doc.customer && (
                              <span
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20"
                                title={`Tagged to customer: ${doc.customer.name}`}
                              >
                                <UserIcon className="w-3 h-3" />
                                {doc.customer.name}
                              </span>
                            )}
                            {getVerificationBadge(doc.verification)}
                          </div>
                        )}
                        {/* Search Match Snippet in List View */}
                        {searchQuery && doc.match_source && doc.match_source !== 'title' && doc.match_snippet && (
                          <p
                            className="text-[10px] text-brand-amber truncate mt-0.5"
                            dir={doc.match_source === 'content' ? 'rtl' : 'ltr'}
                            title={doc.match_snippet}
                          >
                            <MagnifyingGlassIcon className="w-3 h-3 inline mr-1" />
                            {doc.match_snippet}
                          </p>
                        )}
                      </div>
                    </Link>
                    </div>
                  </td>
                  <td className="hidden sm:table-cell px-4 py-3 text-center">
                    <span className="text-sm text-theme-secondary">
                      {doc.pages_count ?? doc.pages?.length ?? 0}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-4 py-3">
                    {getStatusBadge(doc)}
                  </td>
                  <td className="hidden lg:table-cell px-4 py-3">
                    <span className="text-sm text-theme-secondary">{doc.uploaded_by || '—'}</span>
                  </td>
                  <td className="hidden lg:table-cell px-4 py-3">
                    <span className="text-xs text-theme-muted">
                      {new Date(doc.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {/* Extracting state */}
                      {!doc.pages_ready && doc.pages_count === 0 && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 text-blue-400 border border-blue-500/20 rounded-lg">
                          <div className="w-4 h-4 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
                          <span className="text-xs">Extracting...</span>
                        </div>
                      )}
                      {/* Process button - only for own docs */}
                      {isOwnDoc(doc) && !doc.is_processed && !processingIds.includes(doc.id) && doc.pages_ready && doc.pages_count > 0 && (
                        <button
                          type="button"
                          onClick={(e) => handleProcess(e, doc.id)}
                          className="p-2 rounded-lg text-theme-muted hover:text-brand-orange hover:bg-brand-orange/10 transition-colors"
                          title="Run OCR"
                        >
                          <PlayIcon className="w-4 h-4" />
                        </button>
                      )}
                      {/* Processing state - only for own docs */}
                      {isOwnDoc(doc) && isProcessing(doc.id) && !doc.is_processed && (
                        <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-brand-orange/10 to-brand-amber/10 text-brand-orange border border-brand-orange/20 rounded-lg">
                          <div className="w-4 h-4 border-2 border-brand-orange/30 border-t-brand-orange rounded-full animate-spin" />
                          <span className="text-sm font-semibold">{Math.round(getProgress(doc.id))}%</span>
                          <span className="text-xs opacity-75">
                            {processingDocuments[doc.id]?.startedAt
                              ? formatElapsed(processingDocuments[doc.id].startedAt)
                              : processingPageInfo[doc.id]
                                ? `${processingPageInfo[doc.id].processed}/${processingPageInfo[doc.id].total} pages`
                                : 'Processing...'}
                          </span>
                        </div>
                      )}
                      {isOwnDoc(doc) && (
                        <Link
                          to={`/documents/${doc.slug || doc.id}/crop`}
                          className="p-2 rounded-lg text-theme-muted hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                          title="Edit pages"
                        >
                          <PencilSquareIcon className="w-4 h-4" />
                        </Link>
                      )}
                      {isOwnDoc(doc) && (
                        <Link
                          to={`/documents/${doc.slug || doc.id}`}
                          className="p-2 rounded-lg text-theme-muted hover:text-brand-orange hover:bg-brand-orange/10 transition-colors"
                          title="View"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </Link>
                      )}
                      {isOwnDoc(doc) && (
                        <button
                          onClick={() => setAssignDoc(doc)}
                          className={`p-2 rounded-lg transition-colors ${doc.customer ? 'text-blue-400 hover:bg-blue-500/10' : 'text-theme-muted hover:text-blue-400 hover:bg-blue-500/10'}`}
                          title={doc.customer ? `Customer: ${doc.customer.name} — click to change` : 'Tag to a customer'}
                        >
                          <UserIcon className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(doc.id)}
                        className="p-2 rounded-lg text-theme-muted hover:text-status-error hover:bg-status-error/10 transition-colors"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-3">
          <nav className="flex items-center gap-1 bg-theme-tertiary rounded-lg border border-theme-primary p-1 transition-colors duration-300">
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => {
                  const params = { ...currentParamsRef.current, page };
                  currentParamsRef.current = params;
                  fetchDocuments(params);
                }}
                className={`
                  min-w-[32px] h-8 rounded-md text-sm font-medium transition-all duration-200
                  ${pagination.page === page
                    ? 'bg-gradient-to-r from-brand-orange to-brand-amber text-white shadow-md shadow-brand-orange/25'
                    : 'text-theme-muted hover:text-theme-primary hover:bg-theme-elevated'
                  }
                `}
              >
                {page}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Batch Process Modal with Email Notification */}
      {showBatchProcessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className="bg-theme-tertiary rounded-2xl shadow-2xl max-w-md w-full border border-theme-primary animate-scaleIn">
            <div className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-brand-orange/20 rounded-lg">
                  <PlayIcon className="w-6 h-6 text-brand-orange" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-theme-primary">Batch Process Documents</h3>
                  <p className="text-sm text-theme-muted">Process multiple documents with OCR</p>
                </div>
              </div>

              <div className="bg-theme-elevated rounded-lg p-3 mb-4 space-y-2 text-sm text-theme-secondary">
                <p className="font-medium text-theme-primary">
                  Process {pendingBatchDocs.length} document(s)?
                </p>
                <ul className="space-y-1.5 text-xs">
                  <li className="flex items-start gap-2">
                    <span className="text-brand-orange mt-0.5">📄</span>
                    <span>{pendingBatchDocs.length} unprocessed documents will be processed</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-amber-500 mt-0.5">⏱️</span>
                    <span>Processing may take several minutes depending on document size</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-status-info mt-0.5">ℹ️</span>
                    <span>You can monitor progress in the documents list</span>
                  </li>
                </ul>
              </div>

              {/* Email Notification Checkbox */}
              <label className="flex items-start gap-3 p-3 bg-theme-elevated rounded-lg cursor-pointer hover:bg-theme-surface transition-colors mb-4">
                <input
                  type="checkbox"
                  checked={batchEmailNotification}
                  onChange={(e) => setBatchEmailNotification(e.target.checked)}
                  className="mt-0.5 w-4 h-4 text-brand-orange border-theme-primary rounded focus:ring-brand-orange focus:ring-2"
                />
                <div className="flex-1">
                  <span className="text-sm font-medium text-theme-primary block">Email me when processing completes</span>
                  <span className="text-xs text-theme-muted">Get notified when all documents finish processing</span>
                </div>
              </label>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowBatchProcessModal(false);
                    setPendingBatchDocs([]);
                    setBatchEmailNotification(false);
                  }}
                  className="flex-1 px-4 py-2 bg-theme-elevated text-theme-primary font-medium rounded-lg hover:bg-theme-surface border border-theme-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={executeBatchProcessing}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-brand-orange to-brand-amber text-white font-medium rounded-lg hover:opacity-90 transition-opacity shadow-md shadow-brand-orange/25"
                >
                  Start Processing
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Assign Modal */}
      {assignDoc && (
        <AssignCustomerModal
          doc={assignDoc}
          customers={smartOptions.customers}
          onAssign={(payload) => handleAssignCustomer(assignDoc, payload)}
          onClose={() => setAssignDoc(null)}
        />
      )}
    </div>
  );
}

// =============================================================================
// AssignCustomerModal — tag / remap / untag a document's customer.
// Type a name: matching customers filter live; hitting Assign uses the typed
// name (get-or-create server-side), or click an existing customer to use it.
// =============================================================================
function AssignCustomerModal({ doc, customers, onAssign, onClose }) {
  const [query, setQuery] = useState(doc.customer?.name || '');
  const q = query.trim().toLowerCase();
  const matches = q
    ? customers.filter((c) => c.name.toLowerCase().includes(q))
    : customers;
  const exact = customers.find((c) => c.name.toLowerCase() === q);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-theme-tertiary rounded-2xl shadow-2xl max-w-sm w-full border border-theme-primary"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center">
              <UserIcon className="w-4 h-4 text-blue-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-theme-primary">Tag to customer</h3>
              <p className="text-xs text-theme-muted truncate">{getDisplayTitle(doc)}</p>
            </div>
          </div>

          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && query.trim()) onAssign({ customer_name: query.trim() });
              if (e.key === 'Escape') onClose();
            }}
            placeholder="Type a customer name…"
            autoFocus
            className="w-full mt-3 px-3 py-2 rounded-lg bg-theme-elevated border border-theme-primary text-theme-primary placeholder-theme-muted text-sm focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20 outline-none"
          />

          {/* Existing customers (filtered by input) */}
          <div className="mt-2 max-h-44 overflow-y-auto rounded-lg border border-theme-primary divide-y divide-theme-primary">
            {matches.length === 0 && (
              <p className="px-3 py-2.5 text-xs text-theme-muted">
                {q ? 'No matching customer — Assign will create it.' : 'No customers yet — type a name to create one.'}
              </p>
            )}
            {matches.map((c) => (
              <button
                key={c.id}
                onClick={() => onAssign({ customer_id: c.id })}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-theme-elevated transition-colors ${doc.customer?.id === c.id ? 'text-blue-400 font-medium' : 'text-theme-primary'}`}
              >
                <span className="truncate">{c.name}</span>
                <span className="text-[10px] text-theme-muted shrink-0 ml-2">{c.count} docs</span>
              </button>
            ))}
          </div>

          <div className="flex gap-2 mt-4">
            {doc.customer && (
              <button
                onClick={() => onAssign({ customer_id: null })}
                className="px-3 py-2 rounded-lg text-xs font-medium text-status-error hover:bg-status-error/10 transition-colors"
              >
                Remove tag
              </button>
            )}
            <div className="flex-1" />
            <button
              onClick={onClose}
              className="px-3 py-2 rounded-lg text-xs font-medium text-theme-secondary hover:bg-theme-elevated transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => query.trim() && onAssign(exact ? { customer_id: exact.id } : { customer_name: query.trim() })}
              disabled={!query.trim()}
              className="px-4 py-2 rounded-lg text-xs font-semibold bg-brand-orange text-white hover:bg-brand-amber disabled:opacity-40 transition-colors"
            >
              {exact ? 'Assign' : q ? 'Create & assign' : 'Assign'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
